import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { API_URL } from '../../api';
import { shortYear } from '../../constants/yearGroups';
import { useConfirm } from '../../components/ConfirmDialog';

// Tell the sidebar badge to refresh. Cheap browser-level pub/sub — avoids
// threading a context provider through the whole app for this one signal.
const pingUnreadChanged = () =>
    window.dispatchEvent(new Event('contact-enquiries:unread-changed'));

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const TYPE_LABEL = { tutoring: 'Tutoring enquiry', consultation: 'Consultation booking' };
const TYPE_COLOR = { tutoring: 'info', consultation: 'success' };

const STATUS_LABEL = {
    new: 'New',
    contacted: 'Contacted',
    converted: 'Account created',
    archived: 'Archived',
};
const STATUS_COLOR = {
    new: 'primary',
    contacted: 'info',
    converted: 'success',
    archived: 'default',
};

const Field = ({ label, value, full }) => (
    <Box sx={{ display: 'flex', flexDirection: full ? 'column' : 'row', justifyContent: 'space-between', gap: 1, py: 0.75 }}>
        <Typography color="text.secondary" sx={{ minWidth: 220 }}>{label}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', textAlign: full ? 'left' : 'right' }}>{value || '—'}</Typography>
    </Box>
);

export default function ContactEnquiryDetailPage() {
    const confirm = useConfirm();
    const { id } = useParams();
    const navigate = useNavigate();
    const [enquiry, setEnquiry] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const [replyOpen, setReplyOpen] = useState(false);
    const [replySubject, setReplySubject] = useState('');
    const [replyBody, setReplyBody] = useState('');
    const [replySending, setReplySending] = useState(false);

    // Note-adding UI state.
    const [noteDraft, setNoteDraft] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    const openReply = () => {
        setReplySubject(`Re: your ${enquiry?.enquiryType || ''} enquiry`);
        setReplyBody(`<p>Dear ${enquiry?.firstName || ''},</p><p></p>`);
        setReplyOpen(true);
    };

    const sendReply = async () => {
        if (!replySubject.trim() || !replyBody.trim()) {
            setError('Subject and body are required.');
            return;
        }
        setReplySending(true);
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/contact-enquiries/${id}/reply`,
                { subject: replySubject.trim(), htmlBody: replyBody },
                authHeaders()
            );
            setReplyOpen(false);
            setMessage('Reply sent.');
            await load();
            pingUnreadChanged();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send reply.');
        } finally {
            setReplySending(false);
        }
    };

    const load = async () => {
        const { data } = await axios.get(`${API_URL}/api/admin/contact-enquiries/${id}`, authHeaders());
        setEnquiry(data);
    };

    useEffect(() => {
        load().catch((err) => setError(err?.response?.data?.message || 'Failed to load enquiry.'));
    }, [id]);

    const toggleRead = async () => {
        setBusy(true);
        try {
            await axios.patch(`${API_URL}/api/admin/contact-enquiries/${id}/read`, { read: !enquiry.read }, authHeaders());
            await load();
            pingUnreadChanged();
            setMessage(enquiry.read ? 'Marked unread.' : 'Marked read.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const addNote = async () => {
        const text = noteDraft.trim();
        if (!text) return;
        setSavingNote(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/contact-enquiries/${id}/notes`,
                { text },
                authHeaders()
            );
            setEnquiry(data);
            setNoteDraft('');
            setMessage('Note added.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add note.');
        } finally {
            setSavingNote(false);
        }
    };

    const deleteNote = async (noteId) => {
        if (!(await confirm({ title: 'Delete note', message: 'Delete this note?', confirmLabel: 'Delete', danger: true }))) return;
        setBusy(true);
        try {
            const { data } = await axios.delete(
                `${API_URL}/api/admin/contact-enquiries/${id}/notes/${noteId}`,
                authHeaders()
            );
            setEnquiry(data);
            setMessage('Note deleted.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete note.');
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (status) => {
        setBusy(true);
        setError('');
        try {
            await axios.patch(
                `${API_URL}/api/admin/contact-enquiries/${id}/status`,
                { status },
                authHeaders()
            );
            await load();
            setMessage(`Marked as ${STATUS_LABEL[status] || status}.`);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const addToWaitingList = async () => {
        if (!(await confirm({
            title: 'Add to waiting list',
            message: 'Create a waiting-list entry from this enquiry?',
            confirmLabel: 'Add to waiting list',
        }))) return;
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/contact-enquiries/${id}/add-to-waiting-list`,
                {},
                authHeaders()
            );
            setEnquiry(data.enquiry);
            setMessage(data.reused
                ? 'Already on the waiting list — opened existing entry.'
                : 'Added to the waiting list.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const convertToParent = async () => {
        if (!(await confirm({
            title: 'Send account invitation',
            message:
                "Create a parent account for this enquiry? The parent will be emailed a link to set their password. " +
                "(The account is pre-approved — we've already vetted them here.)",
            confirmLabel: 'Send invitation',
        }))) return;
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/contact-enquiries/${id}/convert-to-parent`,
                {},
                authHeaders()
            );
            setEnquiry(data.enquiry);
            setMessage(data.reused
                ? 'Parent account already exists — opened existing.'
                : 'Parent account created + welcome email sent.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!(await confirm({ title: 'Delete enquiry', message: 'This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
        setBusy(true);
        try {
            await axios.delete(`${API_URL}/api/admin/contact-enquiries/${id}`, authHeaders());
            pingUnreadChanged();
            navigate('/contact-enquiries');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
            setBusy(false);
        }
    };

    if (!enquiry) {
        return (
            <Container>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            </Container>
        );
    }

    const isConsultation = enquiry.enquiryType === 'consultation';

    const status = enquiry.status || 'new';
    const isArchived = status === 'archived';
    const isConverted = status === 'converted' || !!enquiry.convertedToAccountId;

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                    <Typography variant="h4">{enquiry.firstName} {enquiry.surname}</Typography>
                    <Chip label={TYPE_LABEL[enquiry.enquiryType] || enquiry.enquiryType} color={TYPE_COLOR[enquiry.enquiryType] || 'default'} size="small" />
                    <Chip label={STATUS_LABEL[status] || status} color={STATUS_COLOR[status] || 'default'} size="small" />
                    {!enquiry.read && <Chip label="Unread" color="error" size="small" />}
                </Stack>
                <Button component={RouterLink} to="/contact-enquiries" variant="text" size="small">
                    ← Back
                </Button>
            </Stack>

            {/* Action row — grouped by purpose:
                  primary = the main communication actions (Reply / Invoice)
                  secondary = state changes (send invitation / add to waitlist)
                  tertiary = utility (mark read/unread, archive/unarchive, delete) */}
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
                <Button onClick={openReply} variant="contained" sx={{ color: '#fff !important' }}>
                    Reply
                </Button>
                <Button
                    component={RouterLink}
                    to={`/invoices/new?enquiryId=${enquiry._id}`}
                    variant="outlined"
                >
                    Create invoice
                </Button>
                {!isConverted && (
                    <Button
                        onClick={convertToParent}
                        disabled={busy}
                        variant="contained"
                        color="secondary"
                        sx={{ color: '#fff !important' }}
                    >
                        Send account invitation
                    </Button>
                )}
                {!enquiry.waitingListEntryId && (
                    <Button onClick={addToWaitingList} disabled={busy} variant="outlined">
                        Add to waiting list
                    </Button>
                )}
                <Button onClick={toggleRead} disabled={busy} variant="outlined">
                    {enquiry.read ? 'Mark unread' : 'Mark read'}
                </Button>
                {!isArchived ? (
                    <Button onClick={() => setStatus('archived')} disabled={busy} variant="outlined">
                        Archive
                    </Button>
                ) : (
                    <Button onClick={() => setStatus('contacted')} disabled={busy} variant="outlined">
                        Unarchive
                    </Button>
                )}
                <Button onClick={remove} disabled={busy} color="error">
                    Delete
                </Button>
            </Stack>

            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Cross-references — where this enquiry ended up. */}
            {(enquiry.convertedToAccountId || enquiry.waitingListEntryId) && (
                <Card sx={{ p: 2, mb: 3, bgcolor: '#F3F4F6' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Linked records</Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                        {enquiry.convertedToAccountId && (
                            <Button
                                component={RouterLink}
                                to={`/parents/${typeof enquiry.convertedToAccountId === 'object'
                                    ? enquiry.convertedToAccountId._id
                                    : enquiry.convertedToAccountId}`}
                                size="small"
                                variant="text"
                            >
                                Open parent account →
                            </Button>
                        )}
                        {enquiry.waitingListEntryId && (
                            <Button
                                component={RouterLink}
                                to="/waiting-list"
                                size="small"
                                variant="text"
                            >
                                Open waiting-list entry →
                            </Button>
                        )}
                    </Stack>
                </Card>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Contact</Typography>
                <Field label="Email" value={enquiry.email} />
                <Field label="Received" value={new Date(enquiry.createdAt).toLocaleString('en-GB')} />
                <Field label="How did you hear about us?" value={enquiry.howHeard} />
                <Field label="Subscribed to updates" value={enquiry.masterclassOptIn ? 'Yes' : 'No'} />
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Child</Typography>
                <Field label="Child's name" value={enquiry.childName} />
                <Field label="Year group" value={enquiry.yearGroup || enquiry.level} />
                <Field label="Subject" value={enquiry.subject} />
                <Field label="School" value={enquiry.school} />
            </Card>

            {isConsultation ? (
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Consultation</Typography>
                    {enquiry.consultationSlotId ? (
                        <Field
                            label="Booked slot"
                            value={`${new Date(enquiry.consultationSlotId.startsAt).toLocaleString('en-GB')} · ${enquiry.consultationSlotId.durationMinutes ?? 15} min`}
                        />
                    ) : (
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            No slot booked yet — follow up by email to schedule.
                        </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Field label="What support does your child need?" value={enquiry.supportNeeded} full />
                </Card>
            ) : (
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Additional information</Typography>
                    <Field label="Notes from parent" value={enquiry.additionalInformation} full />
                </Card>
            )}

            {Array.isArray(enquiry.replies) && enquiry.replies.length > 0 && (
                <Card sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Replies sent ({enquiry.replies.length})</Typography>
                    <Stack spacing={2} divider={<Divider />}>
                        {enquiry.replies.slice().reverse().map((r, i) => (
                            <Box key={i}>
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(r.sentAt).toLocaleString('en-GB')}
                                </Typography>
                                <Typography sx={{ fontWeight: 600, mt: 0.5 }}>{r.subject}</Typography>
                                <Box
                                    sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
                                    dangerouslySetInnerHTML={{ __html: r.htmlBody }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Card>
            )}

            {/* Admin notes — freeform log for calls, follow-up reminders, etc. */}
            <Card sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Notes ({Array.isArray(enquiry.notes) ? enquiry.notes.length : 0})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Internal log — visible only to admin. Not sent to the enquirer.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        multiline
                        minRows={2}
                        placeholder="e.g. Called on 03 Sept, asked about Year 5 English. Follow up in a week."
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        fullWidth
                        size="small"
                    />
                    <Button
                        onClick={addNote}
                        variant="contained"
                        disabled={savingNote || !noteDraft.trim()}
                        sx={{ color: '#fff !important', alignSelf: 'flex-start' }}
                    >
                        {savingNote ? 'Adding…' : 'Add note'}
                    </Button>
                </Stack>
                {Array.isArray(enquiry.notes) && enquiry.notes.length > 0 ? (
                    <Stack spacing={1.5} divider={<Divider />}>
                        {enquiry.notes.slice().reverse().map((n) => (
                            <Box key={n._id || n.at}>
                                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(n.at).toLocaleString('en-GB')}
                                        {n.byAdminEmail ? ` · ${n.byAdminEmail}` : ''}
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => deleteNote(n._id)}
                                        disabled={busy}
                                        sx={{ minWidth: 0 }}
                                    >
                                        Delete
                                    </Button>
                                </Stack>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{n.text}</Typography>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No notes yet.
                    </Typography>
                )}
            </Card>

            <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Reply to {enquiry.firstName} {enquiry.surname}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Sending to: <strong>{enquiry.email}</strong>
                        </Typography>
                        <TextField
                            label="Subject"
                            value={replySubject}
                            onChange={(e) => setReplySubject(e.target.value)}
                            size="small"
                            required
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Message
                            </Typography>
                            <ReactQuill theme="snow" value={replyBody} onChange={setReplyBody} />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplyOpen(false)} disabled={replySending}>Cancel</Button>
                    <Button onClick={sendReply} variant="contained" disabled={replySending}>
                        {replySending ? 'Sending…' : 'Send reply'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
